import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle } from "lucide-react";
import heartAttackIcon from "@/assets/heart-attack-icon.png";
import strokeIcon from "@/assets/stroke-icon.png";
import breathingIcon from "@/assets/breathing-icon.png";
import bleedingIcon from "@/assets/bleeding-icon.png";
import seizureIcon from "@/assets/seizure-icon.png";
import swellingIcon from "@/assets/swelling-icon.png";
import injuryIcon from "@/assets/injury-icon.png";
import childbirthIcon from "@/assets/childbirth-icon.png";
import mentalCrisisIcon from "@/assets/mental-crisis-icon.png";
import suicideAttemptIcon from "@/assets/suicide-attempt-icon.png";

interface EmergencyOption {
  text: string;
  value: string;
  icon?: string;
}

interface EmergencyQuestionProps {
  options: string[] | EmergencyOption[];
  currentAnswer: string;
  onAnswer: (answer: string) => void;
  emergencyNote?: string;
}

const EmergencyQuestion = ({ options, currentAnswer, onAnswer, emergencyNote }: EmergencyQuestionProps) => {
  const getEmergencyIcon = (optionText: string, value?: string) => {
    if (value === "heart_attack" || optionText.includes("heart attack")) return heartAttackIcon;
    if (value === "stroke" || optionText.includes("stroke")) return strokeIcon;
    if (value === "breathing" || optionText.includes("breathing")) return breathingIcon;
    if (value === "bleeding" || optionText.includes("bleeding")) return bleedingIcon;
    if (value === "seizure" || optionText.includes("seizure") || optionText.includes("fit")) return seizureIcon;
    if (value === "swelling" || optionText.includes("swelling")) return swellingIcon;
    if (value === "injury" || optionText.includes("injuries")) return injuryIcon;
    if (value === "childbirth" || optionText.includes("labour") || optionText.includes("childbirth")) return childbirthIcon;
    if (value === "suicide" || optionText.includes("suicide")) return suicideAttemptIcon;
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
        <div className="flex items-center space-x-2 text-red-700 mb-2">
          <AlertTriangle className="h-4 w-4 md:h-5 md:w-5" />
          <span className="font-semibold text-sm md:text-base">Emergency Screening</span>
        </div>
        <p className="text-red-600 text-xs md:text-sm">
          Select any option below if you are experiencing a medical emergency.
        </p>
      </div>
      
      <div className="space-y-3">
        {options.map((option, index) => {
          const isEmergencyOption = typeof option === 'object';
          const optionText = isEmergencyOption ? option.text : option;
          const optionValue = isEmergencyOption ? option.value : option;
          const isEmergency = optionValue !== "none";
          const isSelected = currentAnswer === optionValue;
          const iconSrc = getEmergencyIcon(optionText, isEmergencyOption ? option.value : undefined);
          
          return (
            <Button
              key={index}
              variant={isSelected ? "default" : "outline"}
              className={`w-full text-left justify-start p-4 h-auto min-h-[48px] whitespace-normal transition-all ${
                isSelected 
                  ? isEmergency 
                    ? "bg-red-600 hover:bg-red-700 text-white transform scale-[1.02]" 
                    : "bg-green-600 hover:bg-green-700 text-white transform scale-[1.02]"
                  : isEmergency 
                    ? "hover:bg-red-50 hover:border-red-200 border-red-100" 
                    : "hover:bg-green-50 hover:border-green-200 border-green-100"
              }`}
              onClick={() => onAnswer(optionValue)}
            >
              <div className="flex items-center space-x-3 w-full">
                {iconSrc && (
                  <img 
                    src={iconSrc} 
                    alt={`${optionText} icon`}
                    className="w-6 h-6 md:w-8 md:h-8 flex-shrink-0"
                  />
                )}
                <div className="flex items-center flex-1">
                  {isSelected && <CheckCircle className="mr-2 h-4 w-4" />}
                  <span>{optionText}</span>
                </div>
              </div>
            </Button>
          );
        })}
      </div>
      
      {emergencyNote && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-blue-700 text-sm">
            {emergencyNote}
          </p>
        </div>
      )}
    </div>
  );
};

export default EmergencyQuestion;